import type { ReactNode } from "react";
import type { ModuleDescriptor } from "src/app/moduleDescriptor";
import { ModuleAccessDenied } from "src/components/ModuleAccessDenied/ModuleAccessDenied";
import { ModuleAccessUnavailable } from "src/components/ModuleAccessUnavailable/ModuleAccessUnavailable";
import { useSession } from "src/features/session";
import { canAccess, knownEntitlements } from "./moduleAccess";

interface ModuleGuardProps {
  module: ModuleDescriptor;
  children: ReactNode;
}

/**
 * Renders a module's page only when the user may open it. Otherwise it shows,
 * inside the shell so the header and drawer stay: access denied when the user
 * lacks the entitlement, or a retry when the session failed to load and access
 * can't be checked (fail closed, but without blaming permissions for an outage).
 */
export function ModuleGuard({ module, children }: ModuleGuardProps) {
  const session = useSession();
  if (!module.requiredEntitlements) return children;
  const entitlements = knownEntitlements(session);
  if (entitlements === null) {
    return (
      <ModuleAccessUnavailable onRetry={session.status === "error" ? session.retry : undefined} />
    );
  }
  if (canAccess(module, entitlements)) return children;
  return <ModuleAccessDenied pageName={module.nav?.label} />;
}
